import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { useState, useMemo, useEffect } from "react";
import { useForm, useFieldArray, FormProvider, Controller } from "react-hook-form";
import { RadioGroup, FormControlLabel } from "@mui/material";
import StyledTooltip from "../../../../../components/styled-tooltip/styled-tooltip.js";
import { AppointmentCancellation, AppointmentState } from "../../../appointment-details/appointment-details.types.js";
import CancelIcon from "../../assets/icons/Cancel.js";
import ControlledIcon from "../../assets/icons/Controlled.js";
import InClinicIcon from "../../assets/icons/InClinic.js";
import InTreatmentIcon from "../../assets/icons/InTreatment.js";
import Open from "../../assets/icons/NotInClinic.js";
import TreatmentCompleteIcon from "../../assets/icons/TreatmentComplete.js";
import TreatmentRoomIcon from "../../assets/icons/TreatmentRoom.js";
import WaitingIcon from "../../assets/icons/Waiting.js";
import { useDidUpdate } from "../../../../../hooks/useDidUpdate.js";
import { toTimeString } from "../../../../../utils/time/time.js";
import { parseISO } from "date-fns";
import { useDebouncedCallback } from "use-debounce";
import useStatusChange from "../../hooks/useStatusChange.js";
import { useLabels } from "../../labels.provider.js";
import { useRooms } from "../../room-provider.js";
import AppointmentStatus from "../appointment-status/appointment-status.js";
import AppointmentStatusControlled from "../appointment-status-controlled/appointment-status-controlled.js";
import AppointmentStatusRadio from "../appointment-status-radio/appointment-status-radio.js";
import { INPUT_DELAY } from "./appointment-status-select.constants.js";
import { getCheckedStatus, toFormData, toCancellationType } from "./appointment-status-select.helpers.js";
import { StatusContainer, StatusButton, RoomName, ArrowUp, ArrowDown, StatusPopover, CancellationRadioWrapper, RadioTypography, CancelationRadio, ControlledContainer } from "./appointment-status-select.styles.js";
import "@tanstack/react-query";
import "../AxiosProvider.js";
import "axios";
import "../../query-key-provider.js";
import "../../assets/icons/ControlledCheckIcon.js";
import "../../assets/icons/ControlledDoubleCheckIcon.js";
import "../../assets/icons/Open.js";
import "../appointment-status/appointment-status.styles.js";
import "@mui/material/styles";
import "react-imask";
import "../appointment-status-controlled/appointment-status-controlled.styles.js";
import "../room-select/room-select.js";
import "../../../../../components/menu-item/menu-item.js";
import "@mui/material/MenuItem/index.js";
import "../../../../../constants/create-theme/create-theme.js";
import "../../../../../constants/colors/palette.js";
import "../../../../../themes/shadows/shadows.js";
import "../../../../../themes/typography/typography.js";
import "../../../../../themes/typography/utils/defaultVariantMapping/defaultVariantMapping.js";
import "../../../../../themes/typography/utils/typographyVariants/typographyVariants.js";
import "../../../../../themes/button/button.js";
import "../../../../../themes/button/styles/external/bordered.styles.js";
import "color-alpha";
import "../../../../../themes/button/styles/external/filled.styles.js";
import "../../../../../themes/button/styles/internal/bordered.styles.js";
import "../../../../../themes/button/styles/internal/borderless.styles.js";
import "../../../../../themes/button/styles/internal/filled.styles.js";
import "../../../../../themes/button/styles/internal/shadowed.styles.js";
import "../../../../../themes/checkbox/checkbox.js";
import "../../../../../themes/checkbox/components/CheckedIcon/CheckedIcon.js";
import "@mui/material/SvgIcon/index.js";
import "../../../../../themes/checkbox/components/IndeterminateIcon/IndeterminateIcon.js";
import "../../../../../themes/external-theme/external-theme.js";
import "../../../../../constants/breakpoints/breakpoints.js";
import "../../../../../themes/alert/alert.js";
import "../../../../../themes/alert/components/ErrorIcon/ErrorIcon.js";
import "../../../../../themes/alert/components/InfoIcon/InfoIcon.js";
import "../../../../../themes/alert/components/SuccessIcon/SuccessIcon.js";
import "../../../../../themes/alert/components/WarningIcon/WarningIcon.js";
import "../../../../../themes/form-control-label/form-control-label.js";
import "../../../../../themes/radio/radio.js";
import "../../../../../themes/switch/switch.js";
import "../../../../../themes/pickers-popper/pickers-popper.js";
import "../../../../../components/menu-item/components/menu-item-counter/menu-item-counter.js";
import "../../../../../components/menu-item/assets/icons/minus-circle.js";
import "../../../../../components/menu-item/assets/icons/plus-circle.js";
import "../../../../../components/menu-item/components/menu-item-counter/menu-item-counter.styles.js";
import "../../../../../components/menu-item/menu-item.styles.js";
import "../../../../../components/avatar/avatar.js";
import "@mui/material/Avatar/index.js";
import "../../../../../components/avatar/avatar.styles.js";
import "../../../../../components/icons/MainIcon/MainIcon.js";
import "../room-select/room-select.styles.js";
import "@mui/icons-material";
import "../appointment-status-radio/appointment-status-radio.styles.js";
const cancellationSelectOptions = [
  AppointmentCancellation.NO_SHOW,
  AppointmentCancellation.CANCELED,
  AppointmentCancellation.CANCELED_BY_PRACTICE
];
const AppointmentStatusSelect = ({
  state,
  stateDates,
  cancellation,
  cancellationReason,
  roomReferenceId,
  referenceId
}) => {
  const { mutateAsync: updateStatus, isLoading, error } = useStatusChange();
  const { labels } = useLabels();
  const { rooms } = useRooms();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [showLoadingStatus, setShowLoading] = useState(null);
  const steps = useMemo(
    () => [
      {
        position: 0,
        Icon: Open,
        label: labels.appointment.status.open,
        name: AppointmentState.OPEN
      },
      {
        position: 1,
        Icon: InClinicIcon,
        label: labels.appointment.status.inClinic,
        name: AppointmentState.IN_CLINIC
      },
      {
        position: 2,
        Icon: WaitingIcon,
        label: labels.appointment.status.inWaitingRoom,
        name: AppointmentState.WAITING
      },
      {
        position: 3,
        Icon: TreatmentRoomIcon,
        label: labels.appointment.status.inTreatmentRoom,
        name: AppointmentState.IN_TREATMENT_ROOM,
        hasRoom: true
      },
      {
        position: 4,
        Icon: InTreatmentIcon,
        label: labels.appointment.status.inTreatment,
        name: AppointmentState.IN_TREATMENT
      },
      {
        position: 5,
        Icon: TreatmentCompleteIcon,
        label: labels.appointment.status.treated,
        name: AppointmentState.TREATED
      },
      {
        position: 6,
        Icon: CancelIcon,
        label: labels.appointment.status.cancelled,
        name: AppointmentState.CANCELED
      },
      {
        position: 7,
        Icon: ControlledIcon,
        label: labels.appointment.status.controlled,
        name: AppointmentState.CONTROLLED
      }
    ],
    [labels]
  );
  const { control, watch, setValue, register, handleSubmit, reset, ...form } = useForm({
    defaultValues: {
      checkedStatus: getCheckedStatus(steps, {
        cancellation,
        stateDates
      }),
      statusSelect: toFormData(steps, stateDates),
      roomReferenceId,
      cancellationType: toCancellationType(cancellation),
      stateControlled: stateDates.stateControlled ? toTimeString(parseISO(stateDates.stateControlled)) : null,
      stateControlledAccounting: stateDates.stateControlledAccounting ? toTimeString(parseISO(stateDates.stateControlledAccounting)) : null,
      controlStepsChecked: Boolean(stateDates.stateControlled != null && stateDates.stateControlledAccounting != null),
      cancellationReason
    }
  });
  const { fields, replace } = useFieldArray({
    control,
    name: "statusSelect"
  });
  const checkedStatus = watch(`checkedStatus`);
  const roomRefId = watch(`roomReferenceId`);
  const cancellationType = watch("cancellationType");
  const stateControlled = watch("stateControlled");
  const stateControlledAccounting = watch("stateControlledAccounting");
  const selectedRoom = useMemo(
    () => roomRefId ? rooms.find((room) => room.referenceId === roomRefId) : void 0,
    [rooms, roomRefId]
  );
  const statusTooltipTextKey = useMemo(() => {
    const foundStep = steps.find((step) => step.name === checkedStatus);
    if (!foundStep)
      return labels.appointment.tooltip;
    if (checkedStatus && [AppointmentState.IN_TREATMENT_ROOM, AppointmentState.IN_TREATMENT].includes(checkedStatus) && roomRefId) {
      return (selectedRoom == null ? void 0 : selectedRoom.name) || labels.appointment.status.inTreatmentRoom;
    }
    if (foundStep.name === AppointmentState.CONTROLLED) {
      if (stateControlled && stateControlledAccounting) {
        return `${labels.appointment.status.controlStep.one} ${labels.and} ${labels.appointment.status.controlStep.two}`;
      }
      if (stateControlled) {
        return labels.appointment.status.controlStep.one;
      }
      if (stateControlledAccounting) {
        return labels.appointment.status.controlStep.two;
      }
    }
    return foundStep.label;
  }, [checkedStatus, selectedRoom == null ? void 0 : selectedRoom.name, roomRefId, labels, steps, stateControlled, stateControlledAccounting]);
  const { stateInClinicStart, stateTreatmentStart, stateInTreatmentRoomStart, stateWaitingStart, stateTreatmentEnd } = stateDates;
  const isInClinic = !!stateInClinicStart && !stateTreatmentStart && !stateInTreatmentRoomStart && !stateWaitingStart && !stateTreatmentEnd;
  useDidUpdate(() => {
    if (checkedStatus !== AppointmentState.CANCELED && cancellationType) {
      setValue("cancellationType", "");
      setValue("cancellationReason", "");
    }
  }, [checkedStatus, setValue, cancellationType]);
  useEffect(() => {
    const inClinicStatus = Boolean(state === AppointmentState.OPEN && stateInClinicStart);
    const controlledStatus = Boolean(
      state === AppointmentState.TREATED && (stateDates.stateControlled || stateDates.stateControlledAccounting)
    );
    if (inClinicStatus) {
      return setValue("checkedStatus", AppointmentState.IN_CLINIC);
    }
    if (controlledStatus) {
      return setValue("checkedStatus", AppointmentState.CONTROLLED);
    }
    setValue("checkedStatus", state);
    if (error)
      reset();
  }, [
    state,
    stateInClinicStart,
    stateDates.stateControlled,
    stateDates.stateControlledAccounting,
    error,
    reset,
    setValue
  ]);
  useEffect(() => {
    if (!isLoading)
      setShowLoading(null);
  }, [isLoading]);
  useDidUpdate(() => {
    const statusSelect = toFormData(steps, stateDates);
    setValue("statusSelect", statusSelect);
  }, [stateInClinicStart, stateTreatmentStart, stateInTreatmentRoomStart, stateWaitingStart, stateTreatmentEnd]);
  useDidUpdate(() => {
    const stateControlled2 = stateDates.stateControlled ? toTimeString(parseISO(stateDates.stateControlled)) : null;
    const stateControlledAccounting2 = stateDates.stateControlledAccounting ? toTimeString(parseISO(stateDates.stateControlledAccounting)) : null;
    setValue("stateControlled", stateControlled2);
    setValue("stateControlledAccounting", stateControlledAccounting2);
  }, [stateDates.stateControlled, stateDates.stateControlledAccounting, setValue]);
  useDidUpdate(() => {
    setValue("roomReferenceId", roomReferenceId);
  }, [roomReferenceId, setValue]);
  const handleChange = (data) => {
    updateStatus({
      referenceId,
      ...data
    });
  };
  const debouncedHandleOnChange = useDebouncedCallback(
    (data) => handleChange(data),
    INPUT_DELAY
  );
  const handleStatusButtonClick = (e) => {
    setIsPopoverOpen((prevIsOpen) => !prevIsOpen);
    setPopoverAnchorEl(isPopoverOpen ? null : e.currentTarget);
  };
  const handlePopoverClick = (e) => {
    e.stopPropagation();
  };
  const handleClose = () => {
    setIsPopoverOpen(false);
    setPopoverAnchorEl(null);
  };
  const handleCancelRadioChange = (onFieldChange) => (event) => {
    onFieldChange(event);
    setValue("cancellationReason", "");
    handleSubmit(debouncedHandleOnChange)();
  };
  const updateRadioStatus = (step, data) => {
    var _a, _b, _c, _d, _e;
    const timeStamp = (_b = (_a = data.statusSelect) == null ? void 0 : _a.find(({ name }) => name === step.name)) == null ? void 0 : _b.time;
    if (!timeStamp)
      return data;
    let newFields;
    if (step.name === AppointmentState.CANCELED) {
      const stepsToUpdate = steps.filter(({ name }) => name !== AppointmentState.CANCELED).map(({ name }) => name);
      newFields = (_c = data.statusSelect) == null ? void 0 : _c.map((field) => {
        if (field.time && stepsToUpdate.includes(field.name))
          return { ...field, time: void 0 };
        return field;
      });
    } else if (step.name === AppointmentState.CONTROLLED) {
      const stepsToUpdate = steps.filter(({ name }) => name !== AppointmentState.CONTROLLED).map(({ name }) => name);
      newFields = (_d = data.statusSelect) == null ? void 0 : _d.map((field) => {
        if (stepsToUpdate.includes(field.name))
          return { ...field, checked: false };
        return field;
      });
    } else {
      newFields = (_e = data.statusSelect) == null ? void 0 : _e.map((field) => {
        const foundStep = steps.find(({ name: stepName }) => stepName === field.name);
        if (!field.time && field.name !== void 0 && foundStep && foundStep.position < step.position)
          return { ...field, time: timeStamp };
        if (field.time && field.name !== void 0 && foundStep && foundStep.position > step.position)
          return { ...field, time: void 0 };
        return field;
      });
    }
    if (newFields)
      replace(newFields);
    return { ...data, statusSelect: newFields };
  };
  const handleFormChange = (step) => (timeManuallyInputted) => (data) => {
    setShowLoading(step.name);
    const updatedData = timeManuallyInputted ? data : updateRadioStatus(step, data);
    handleChange(updatedData);
  };
  const handleRoomChange = (roomReferenceId2) => (data) => {
    handleChange({ ...data, roomReferenceId: roomReferenceId2 });
  };
  return /* @__PURE__ */ jsxs(
    FormProvider,
    {
      ...form,
      reset,
      handleSubmit,
      setValue,
      watch,
      control,
      register,
      children: [
        /* @__PURE__ */ jsx(StyledTooltip, { title: statusTooltipTextKey, placement: "top", children: /* @__PURE__ */ jsx(StatusContainer, { children: /* @__PURE__ */ jsxs(StatusButton, { isPopoverOpen: false, onClick: handleStatusButtonClick, disableRipple: true, variant: "borderless", children: [
          /* @__PURE__ */ jsx(
            AppointmentStatus,
            {
              isInClinic,
              state,
              cancellation,
              stateControlled,
              stateControlledAccounting
            }
          ),
          roomRefId && checkedStatus && [AppointmentState.IN_TREATMENT_ROOM, AppointmentState.IN_TREATMENT].includes(checkedStatus) && /* @__PURE__ */ jsx(RoomName, { variant: "Lato Caption Bold", noWrap: true, children: selectedRoom == null ? void 0 : selectedRoom.name }),
          isPopoverOpen ? /* @__PURE__ */ jsx(ArrowUp, {}) : /* @__PURE__ */ jsx(ArrowDown, {})
        ] }) }) }),
        /* @__PURE__ */ jsx(
          StatusPopover,
          {
            keepMounted: true,
            open: isPopoverOpen,
            anchorEl: popoverAnchorEl,
            onClick: handlePopoverClick,
            onClose: handleClose,
            anchorOrigin: {
              vertical: "center",
              horizontal: "left"
            },
            transformOrigin: {
              vertical: "center",
              horizontal: "left"
            },
            children: /* @__PURE__ */ jsx(
              Controller,
              {
                name: `checkedStatus`,
                control,
                render: ({ field }) => {
                  return /* @__PURE__ */ jsx(RadioGroup, { ...field, value: field.value, name: "ongoingStatus", children: fields.map((step, index) => {
                    const stepDetails = steps[index];
                    const disabled = stepDetails.name === AppointmentState.CANCELED && checkedStatus === AppointmentState.TREATED || stepDetails.name === AppointmentState.CONTROLLED && checkedStatus !== AppointmentState.TREATED || checkedStatus === AppointmentState.CONTROLLED;
                    const greyedOut = stepDetails.name !== AppointmentState.CANCELED && checkedStatus === AppointmentState.CANCELED;
                    return /* @__PURE__ */ jsxs(Fragment, { children: [
                      /* @__PURE__ */ jsx(
                        AppointmentStatusRadio,
                        {
                          ...stepDetails,
                          name: stepDetails.name,
                          prefix: `statusSelect.${index}`,
                          disabled,
                          grey: greyedOut,
                          hasRoomSelector: stepDetails.hasRoom,
                          isLoading: step.name === showLoadingStatus,
                          onFormChange: handleFormChange(stepDetails),
                          onRoomChange: handleRoomChange
                        },
                        `${step.name}_radio`
                      ),
                      step.name === AppointmentState.CANCELED && checkedStatus === AppointmentState.CANCELED && /* @__PURE__ */ jsx(CancellationRadioWrapper, { children: /* @__PURE__ */ jsx(
                        Controller,
                        {
                          name: "cancellationType",
                          control,
                          render: ({ field: field2 }) => /* @__PURE__ */ jsx(RadioGroup, { name: "cancellationType", children: cancellationSelectOptions.map((reason) => {
                            const isChecked = reason === field2.value;
                            const reasonLabel = labels.appointment.status.cancelReason[reason];
                            return /* @__PURE__ */ jsx(
                              FormControlLabel,
                              {
                                label: /* @__PURE__ */ jsx(RadioTypography, { variant: "Lato Body 2", children: reasonLabel }),
                                control: /* @__PURE__ */ jsx(
                                  CancelationRadio,
                                  {
                                    ...field2,
                                    onChange: handleCancelRadioChange(field2.onChange),
                                    value: reason,
                                    checked: isChecked
                                  }
                                )
                              },
                              reason
                            );
                          }) }, `${step.id}_cancelation_radio_group`)
                        }
                      ) }, `${step.name}_cancelation_wrapper`),
                      step.name === AppointmentState.CONTROLLED && checkedStatus === AppointmentState.CONTROLLED && /* @__PURE__ */ jsx(
                        Controller,
                        {
                          name: "controlStepsChecked",
                          control,
                          render: () => /* @__PURE__ */ jsxs(ControlledContainer, { children: [
                            /* @__PURE__ */ jsx(
                              AppointmentStatusControlled,
                              {
                                label: labels.appointment.status.controlStep.one,
                                name: "stateControlled",
                                disabled: false,
                                isLoading: step.name === showLoadingStatus,
                                onFormChange: handleFormChange(stepDetails)
                              }
                            ),
                            /* @__PURE__ */ jsx(
                              AppointmentStatusControlled,
                              {
                                label: labels.appointment.status.controlStep.two,
                                name: "stateControlledAccounting",
                                disabled: false,
                                isLoading: step.name === showLoadingStatus,
                                onFormChange: handleFormChange(stepDetails)
                              }
                            )
                          ] })
                        },
                        `${step.name}_controlled`
                      )
                    ] });
                  }) });
                }
              }
            )
          }
        )
      ]
    }
  );
};
export {
  AppointmentStatusSelect as default
};
